import { Main as Container } from 'Components/Main';
import { useNavigate, useParams } from 'react-router-dom';
import { Detalhes } from './Details';
import { FormSuccess } from 'Components/Form/Success';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { Dialog, PageHeader, Button } from 'Components/ui';
import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { addOffice, editOffice, getOfficeById } from 'services/offices-service';
import { getUsersName } from 'services/users-service';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { PageHelper } from '../../../Components/PageHelper';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// ─── Styled components ─────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  max-width: 720px;
`;

const CardTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 24px;
  line-height: 1.5;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const OfficesForm = () => {
  const { t } = useTranslation(['company']);
  const selectedTenant = useSelector(useAuthTenant);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const effectivePhoneCountry = phoneCountry ?? selectedTenant?.country;
  const { phonePrefix, countryConfig, allConfigs, isError: isCountryConfigError } = useCountryConfig(effectivePhoneCountry);
  const phoneFormat = countryConfig?.phoneFormat ?? null;
  const { id } = useParams();
  const navigate = useNavigate();
  const [idOffice, setIdOffice] = useState(null);
  const [finished, setFinished] = useState(false);

  const { data: users } = useQuery(['getUsers'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data?.length > 0 ? result?.data : [];
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      companyKey: selectedTenant.slug,
      city: '',
      state: '',
      country: '',
      responsible: '',
      isHeadquarters: false,
      active: true,
    },
  });

  const handleComplete = async () => {
    try {
      const res = id ? await editOffice(id, getValues()) : await addOffice(getValues());
      if (res.status === 200 || res.status === 201) {
        setIdOffice(res.data._id ?? id);
        setFinished(true);
      } else {
        throw new Error('Ocorreu um erro ao tentar salvar/editar o Escritório!');
      }
    } catch (error) {
      Dialog.alert({
        title: t('company:offices.form.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdOffice(null);
    navigate('/offices/add');
  };

  const getOffice = useCallback(async () => {
    try {
      const resOffice = await getOfficeById(id);
      Object.keys(resOffice.data).forEach(key => {
        setValue(key, resOffice.data[key]);
      });
    } catch (error) {
      Dialog.alert({
        title: t('company:offices.form.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  }, [id, setValue]);

  useEffect(() => {
    if (!id) return;
    getOffice();
  }, [id, getOffice]);

  const tourSteps = [
    { target: 'body', content: `Bem-vindo ao formulário de ${id ? 'edição' : 'cadastro'} de Escritório!`, placement: 'center', disableBeacon: true },
    { target: '[data-tour="page-title"]', content: 'Clique aqui no título "Escritórios" para voltar à lista.', placement: 'bottom', title: 'Voltar para Lista' },
    { target: '[data-tour="office-name"]', content: 'Digite o nome do escritório ou filial. Este campo é obrigatório.', placement: 'bottom', title: 'Nome do Escritório' },
    { target: '[data-tour="office-address"]', content: 'Informe o endereço completo onde o escritório está localizado.', placement: 'bottom', title: 'Endereço' },
    { target: '[data-tour="office-phone"]', content: 'Adicione o telefone de contato do escritório.', placement: 'bottom', title: 'Telefone' },
    { target: '[data-tour="form-actions"]', content: `Use "Cancelar" para voltar sem salvar, ou "Salvar" para ${id ? 'atualizar' : 'cadastrar'} o escritório.`, placement: 'top', title: 'Ações do Formulário' },
  ];

  return (
    <Container>
      {!finished && (
        <PageHelper steps={tourSteps} tourKey="offices-form-tour" autoStart={true} showButton={true} />
      )}
      <PageWrap>

        {/* ── Header ─────────────────────────────────────────────── */}
        <PageHeader
          data-tour="page-title"
          title={id ? t('company:offices.form.editTitle') : t('company:offices.form.addTitle')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('company:offices.title'), route: '/offices' },
          ]}
          actions={!finished && (
            <HeaderActions data-tour="form-actions">
              <Button variant="outline" type="button" onClick={() => navigate('/offices')} sx={{ height: '44px', padding: '0 20px' }}>
                {t('common:buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" form="office-form" sx={{ height: '44px', padding: '0 20px' }}>
                {t('company:offices.form.save')}
              </Button>
            </HeaderActions>
          )}
        />

        {/* ── Form card ──────────────────────────────────────────── */}
        <FormCard>
          {!finished && (
            <>
              <CardTitle>{t('company:offices.form.cardTitle')}</CardTitle>
              <CardSubtitle>{t('company:offices.form.cardSubtitle')}</CardSubtitle>
              <Detalhes
                control={control}
                register={register}
                handleComplete={handleComplete}
                handleSubmit={handleSubmit}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                users={users}
                phonePrefix={phonePrefix}
                phoneFormat={phoneFormat}
                countryCode={effectivePhoneCountry}
                allConfigs={allConfigs}
                isCountryConfigError={isCountryConfigError}
                setPhoneCountry={setPhoneCountry}
              />
            </>
          )}
          {finished && (
            <FormSuccess
              title={t('company:offices.form.success')}
              labelStartAgain={t('company:offices.form.addAnother')}
              handleStartAgain={() => handleReset()}
              labelShow={t('company:offices.form.viewDetails')}
              handleShow={() => navigate('/offices/show/' + idOffice)}
            />
          )}
        </FormCard>

      </PageWrap>
    </Container>
  );
};
